import { useState } from "react";
import { Plus, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { useLoad, act } from "@/lib/hooks";
import { api } from "@/lib/api";
import { useAccess } from "@/context/AuthContext";
import { PageHeader, Field } from "@/components/common";

export default function Templates() {
  const [rows, reload] = useLoad("/tenant/templates");
  const [form, setForm] = useState(null);
  const { canWrite } = useAccess();
  const can = canWrite(["tenant_admin", "accountant"]);
  const sum = form ? form.milestones.reduce((s, m) => s + Number(m.percent || 0), 0) : 0;
  const setM = (i, k, v) => setForm({ ...form, milestones: form.milestones.map((m, j) => (j === i ? { ...m, [k]: v } : m)) });

  const save = async () => {
    const body = { name: form.name, milestones: form.milestones.map((m) => ({ label: m.label, percent: Number(m.percent) })) };
    const r = await act(() => (form.id ? api.put(`/tenant/templates/${form.id}`, body) : api.post("/tenant/templates", body)), "Template saved");
    if (r) { setForm(null); reload(); }
  };
  const remove = async (id) => { if (await act(() => api.delete(`/tenant/templates/${id}`), "Template deleted")) reload(); };

  return (
    <>
      <PageHeader title="Payment templates" subtitle="Milestone schedules, calculated as a percentage of the invoice total.">
        {can && <Button data-testid="new-template-button" onClick={() => setForm({ name: "", milestones: [{ label: "Deposit", percent: 50 }, { label: "Balance", percent: 50 }] })} className="bg-[#45BFB4] text-[#13294B] hover:bg-[#389F95] hover:text-white"><Plus className="mr-1 h-4 w-4" />New template</Button>}
      </PageHeader>
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
        {rows?.length === 0 && <p className="text-sm text-slate-400">No templates yet.</p>}
        {rows?.map((t) => (
          <div key={t.id} className="po-card p-5 fade-up" data-testid={`template-card-${t.id}`}>
            <div className="flex items-start justify-between gap-2">
              <h3 className="font-semibold text-slate-900">{t.name}</h3>
              {can && <div className="flex gap-1">
                <Button size="sm" variant="ghost" data-testid={`template-edit-${t.id}`} onClick={() => setForm(t)}>Edit</Button>
                <Button size="sm" variant="ghost" data-testid={`template-delete-${t.id}`} onClick={() => remove(t.id)}><Trash2 className="h-4 w-4" /></Button>
              </div>}
            </div>
            <div className="mt-4 flex h-2 overflow-hidden rounded-full bg-slate-100">
              {t.milestones.map((m, i) => <div key={i} style={{ width: `${m.percent}%` }} className={i % 2 ? "bg-[#45BFB4]" : "bg-[#13294B]"} />)}
            </div>
            <ul className="mt-3 space-y-1 text-sm">
              {t.milestones.map((m, i) => <li key={i} className="flex justify-between"><span>{m.label}</span><span className="tabular-nums text-slate-500">{m.percent}%</span></li>)}
            </ul>
          </div>
        ))}
      </div>
      <Dialog open={!!form} onOpenChange={() => setForm(null)}>
        <DialogContent data-testid="template-dialog">
          <DialogHeader><DialogTitle>{form?.id ? "Edit template" : "New template"}</DialogTitle><DialogDescription className="sr-only">Set the template name and its payment milestones.</DialogDescription></DialogHeader>
          {form && <div className="space-y-3">
            <Field label="Name"><Input data-testid="template-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></Field>
            {form.milestones.map((m, i) => (
              <div key={i} className="flex gap-2">
                <Input data-testid={`milestone-label-${i}`} value={m.label} onChange={(e) => setM(i, "label", e.target.value)} placeholder="Milestone" />
                <Input data-testid={`milestone-percent-${i}`} className="w-24" type="number" value={m.percent} onChange={(e) => setM(i, "percent", e.target.value)} />
                <Button variant="ghost" size="icon" disabled={form.milestones.length === 1} onClick={() => setForm({ ...form, milestones: form.milestones.filter((_, j) => j !== i) })}><Trash2 className="h-4 w-4" /></Button>
              </div>
            ))}
            <Button variant="outline" size="sm" data-testid="milestone-add-button" onClick={() => setForm({ ...form, milestones: [...form.milestones, { label: "", percent: 0 }] })}><Plus className="mr-1 h-4 w-4" />Add milestone</Button>
            <p className={`text-sm ${Math.abs(sum - 100) < 0.01 ? "text-emerald-600" : "text-rose-600"}`} data-testid="template-percent-sum">Total: {sum}% (must equal 100%)</p>
          </div>}
          <DialogFooter><Button data-testid="template-save-button" disabled={!form?.name || Math.abs(sum - 100) > 0.01} onClick={save}>Save template</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
